import "../../App.css";
import { useEffect, useState } from "react";
import CustomPagination from "../custom-pagination/CustomPagination";
import { Box, Card, CardMedia, Chip, Stack, Typography, Avatar } from "@mui/material";
import Preloader from "../preloader/PreLoader";
import { Link, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { NewsStore } from "../../store/newsStore";
import { BASE_URL } from "../../api/api";

const noImage = "/assets/logos/logo.png";

export default function AuthorPage() {
  const { id: authorId } = useParams();
  const { fetchNewsByAuthor, authorNews } = NewsStore();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6; // 6 articles per page
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      if (!authorId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        if (typeof fetchNewsByAuthor === 'function') {
          await fetchNewsByAuthor(authorId, currentPage, pageSize);
        }
      } catch (err) {
        console.error("Failed to fetch author news:", err);
        setError("Unable to load articles. The API endpoint might be incorrect.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [authorId, currentPage, pageSize, fetchNewsByAuthor]);

  // Handle different response structures (array vs object with data property)
  const articles = Array.isArray(authorNews)
    ? authorNews
    : (authorNews?.data && Array.isArray(authorNews.data) ? authorNews.data : []);

  const author = articles.length > 0 ? articles[0].author : null;
  const totalPages = authorNews?.meta?.last_page || 1;
  const totalArticles = authorNews?.meta?.total || articles.length || 0;

  const getImageUrl = (imagePath) => {
    if (!imagePath) return noImage;
    return imagePath.startsWith("http") ? imagePath : `${BASE_URL}${imagePath}`;
  };

  const truncateHtmlText = (html, maxLength = 100) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  };

  if (loading) return <Preloader />;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default", color: "text.primary", pb: 4 ,pt:6}}>
      <div className="profile-container">
        {/* Top Profile Section */}
        {author && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: 3,
              p: { xs: 3, sm: 4 },
              mb: 4,
              borderRadius: 4,
              bgcolor: "background.paper",
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {/* Left/Top: Avatar and Info */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 3 },
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              <Avatar
                src={getImageUrl(author?.profile_image)}
                alt={author?.name || "Author"}
                imgProps={{
                  onError: (e) => {
                    e.target.onerror = null;
                    e.target.src = "/assets/icons/user-profile.avif";
                  },
                }}
                sx={{
                  width: { xs: 90, sm: 110 },
                  height: { xs: 90, sm: 110 },
                  border: "4px solid #ff184e",
                  boxShadow: "0 4px 12px rgba(255,24,78,0.2)",
                }}
              />
              <Box>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  sx={{ color: "text.primary", fontSize: { xs: "24px", sm: "28px" }, mb: 0.5 }}
                >
                  {author?.name || "Author Name"}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>
                  janpathnews24
                </Typography>
              </Box>
            </Box>

            {/* Right/Bottom: Articles Count */}
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "center",
                gap: 1, // Space between Number and "ARTICLES" text
                bgcolor: "rgba(255, 24, 78, 0.08)",
                color: "#ff184e",
                px: { xs: 4, sm: 4 },
                py: { xs: 1.5, sm: 2 },
                borderRadius: 3,
                width: { xs: "100%", sm: "auto" }, // Full width on small screens
              }}
            >
              <Typography variant="h3" fontWeight="900" sx={{ lineHeight: 1 }}>
                {totalArticles}
              </Typography>
              <Typography variant="h6" fontWeight="700" sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                Articles
              </Typography>
            </Box>
          </Box>
        )}

        {/* Articles Grid */}
        <div className="articles-grid">
          {articles.length > 0 ? (
            articles.map((item) => (
              <Link key={item.id} to={`/${item.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                <Card
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardMedia
                    component="img"
                    image={getImageUrl(item.image)}
                    alt={item.title}
                    loading="lazy"
                    sx={{
                      height: { xs: 200, sm: 220, md: 240 },
                      borderRadius: 2,
                      objectFit: getImageUrl(item.image) === noImage ? "contain" : "cover",
                      objectPosition: "center center",
                      bgcolor: getImageUrl(item.image) === noImage ? "rgba(0,0,0,0.05)" : "transparent",
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = noImage;
                      e.target.style.objectFit = "contain";
                      e.target.style.backgroundColor = "rgba(0,0,0,0.05)";
                    }}
                  />

                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                    {item.category?.name && (
                      <Chip
                        label={item.category.name}
                        size="small"
                        sx={{ bgcolor: "#f4f4f4", fontSize: "12px" }}
                      />
                    )}
                  </Stack>

                  <Typography
                    variant="h6"
                    mt={1}
                    fontWeight="bold"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      flexGrow: 1,
                      "&:hover": { color: "#ff184e" }
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",

                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {truncateHtmlText(item.description)}
                  </Typography>

                  <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" fontWeight="bold">
                      {author?.name}
                    </Typography>

                    <Typography variant="caption">•</Typography>

                    <Typography variant="caption" color="text.secondary">
                      {dayjs(item.published_at).format("MMMM D, YYYY")}
                    </Typography>
                  </Box>
                </Card>
              </Link>
            ))
          ) : (
            <Typography color={error ? "error" : "textPrimary"} sx={{ textAlign: 'center', width: '100%', mt: 4 }}>
              {error || "No articles found for this author."}
            </Typography>
          )}
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <CustomPagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        )}
      </div>
    </Box>
  );
}
